import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
} from "react";
import { releaseTalkMic, routeTalkMic, unlockTalkPlayback } from "./helpAudio";
import { talkProbe } from "./talkProbe";

export type TalkRecorderPhase = "idle" | "holding" | "review" | "denied";
export type TalkMicBlock = "insecure" | "unsupported" | "permission" | null;

const MIN_MS = 500;
const TIMER_MS = 60_000;
const MAX_MS = 120_000;

function micAvailability(): Exclude<TalkMicBlock, null> | "ok" {
  if (typeof window !== "undefined" && window.isSecureContext === false)
    return "insecure";
  if (!navigator.mediaDevices?.getUserMedia) return "unsupported";
  return "ok";
}

function iosCapture(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}

function isLive(stream: MediaStream | null): boolean {
  if (!stream?.getTracks) return false;
  const tracks = stream.getAudioTracks?.() ?? stream.getTracks();
  return tracks.some((t) => t.readyState !== "ended");
}

function wavFromPcm(parts: Int16Array[], rate = 16000): Blob | null {
  let samples = 0;
  for (const part of parts) samples += part.length;
  if (!samples) return null;
  const bytes = samples * 2;
  const buf = new ArrayBuffer(44 + bytes);
  const view = new DataView(buf);
  const str = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i++)
      view.setUint8(offset + i, value.charCodeAt(i));
  };
  str(0, "RIFF");
  view.setUint32(4, 36 + bytes, true);
  str(8, "WAVE");
  str(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  str(36, "data");
  view.setUint32(40, bytes, true);
  const pcm = new Int16Array(buf, 44);
  let offset = 0;
  for (const part of parts) {
    pcm.set(part, offset);
    offset += part.length;
  }
  return new Blob([buf], { type: "audio/wav" });
}

let sharedStream: MediaStream | null = null;
let sharedOpening: Promise<MediaStream | null> | null = null;
let micOwners = 0;
let micEpoch = 0;

function watchEnded(stream: MediaStream) {
  const tracks = stream.getAudioTracks?.() ?? stream.getTracks();
  for (const t of tracks) {
    t.addEventListener?.("ended", () => {
      if (sharedStream === stream) sharedStream = null;
    });
  }
}

function releaseSharedMic() {
  micEpoch += 1;
  sharedStream?.getTracks().forEach((t) => t.stop());
  sharedStream = null;
  sharedOpening = null;
}

export function resetTalkMicForTests() {
  releaseSharedMic();
  micOwners = 0;
}

export function armTalkMic(): Promise<MediaStream | null> {
  if (micAvailability() !== "ok") return Promise.resolve(null);
  if (isLive(sharedStream)) return Promise.resolve(sharedStream);
  if (sharedOpening) return sharedOpening;
  const epoch = micEpoch;
  let job!: Promise<MediaStream | null>;
  job = (async (): Promise<MediaStream | null> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (epoch !== micEpoch || micOwners <= 0 || sharedOpening !== job) {
        talkProbe(
          epoch !== micEpoch
            ? "gum-epoch"
            : micOwners <= 0
              ? "gum-owners"
              : "gum-replaced"
        );
        stream.getTracks().forEach((t) => t.stop());
        return null;
      }
      if (isLive(sharedStream)) {
        stream.getTracks().forEach((t) => t.stop());
        return sharedStream;
      }
      watchEnded(stream);
      sharedStream = stream;
      return stream;
    } catch (err) {
      const name = err instanceof Error ? err.name : "throw";
      talkProbe(`gum-${name || "throw"}`);
      return null;
    } finally {
      if (sharedOpening === job) sharedOpening = null;
    }
  })();
  sharedOpening = job;
  return job;
}

export function useTalkRecorder() {
  const [phase, setPhase] = useState<TalkRecorderPhase>("idle");
  const [pressed, setPressed] = useState(false);
  const pressedRef = useRef(false);
  const [capturing, setCapturing] = useState(false);
  const [block, setBlock] = useState<TalkMicBlock>(null);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const started = useRef(0);
  const fingerDown = useRef(0);
  const tick = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);
  const cloneRef = useRef<MediaStream | null>(null);
  const wakeRef = useRef<{ release: () => Promise<void> } | null>(null);
  const phaseRef = useRef<TalkRecorderPhase>("idle");
  const startingRef = useRef(false);
  const pendingStopRef = useRef(false);
  const grantedRef = useRef(false);
  const deadRef = useRef(false);
  const takenRef = useRef<Promise<Blob | null>>(Promise.resolve(null));
  const waitChunk = useRef<(() => void) | null>(null);
  const chunkRef = useRef<((blob: Blob) => void) | null>(null);
  const pcmRef = useRef<((pcm: ArrayBuffer) => void) | null>(null);
  const shipRef = useRef<((blob: Blob) => void) | null>(null);
  const pcmQueue = useRef<ArrayBuffer[]>([]);
  const pcmParts = useRef<Int16Array[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const procRef = useRef<ScriptProcessorNode | null>(null);
  const keepAliveRef = useRef<OscillatorNode | null>(null);
  const pullRef = useRef(0);
  const scriptHeard = useRef(false);

  const setPhaseNow = (next: TalkRecorderPhase) => {
    phaseRef.current = next;
    setPhase(next);
  };

  const releaseWake = () => {
    void wakeRef.current?.release().catch(() => {});
    wakeRef.current = null;
  };

  const stopPcm = () => {
    window.clearInterval(pullRef.current);
    pullRef.current = 0;
    const proc = procRef.current;
    procRef.current = null;
    try {
      proc?.disconnect();
    } catch {
      /* ignore */
    }
    const osc = keepAliveRef.current;
    keepAliveRef.current = null;
    try {
      osc?.stop();
    } catch {
      /* ignore */
    }
    try {
      osc?.disconnect();
    } catch {
      /* ignore */
    }
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    void ctx?.close().catch(() => {});
  };

  const primeCaptureContext = () => {
    const AC =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) return null;
    try {
      if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
        audioCtxRef.current = new AC();
      }
      const ctx = audioCtxRef.current;
      void ctx.resume().catch(() => {});
      return ctx;
    } catch {
      return audioCtxRef.current;
    }
  };

  const storePcm = (input: Float32Array, rate: number) => {
    const ratio = (rate || 48000) / 16000;
    const len = Math.floor(input.length / ratio);
    if (!len) return;
    const pcm = new Int16Array(len);
    for (let i = 0; i < len; i++) {
      const s = Math.max(-1, Math.min(1, input[Math.floor(i * ratio)] || 0));
      pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    if (!pcm.some((n) => n !== 0)) return;
    pcmParts.current.push(pcm);
    const copy: ArrayBuffer = pcm.buffer.slice(
      pcm.byteOffset,
      pcm.byteOffset + pcm.byteLength
    );
    pcmRef.current?.(copy);
  };

  const startPcm = (stream: MediaStream) => {
    const ctx = audioCtxRef.current ?? primeCaptureContext();
    if (!ctx) return;
    void ctx.resume().catch(() => {});
    scriptHeard.current = false;
    try {
      const rate = ctx.sampleRate || 48000;
      const src = ctx.createMediaStreamSource(stream);
      const gain = ctx.createGain();
      gain.gain.value = 0.00001;
      try {
        const proc = ctx.createScriptProcessor(4096, 1, 1);
        procRef.current = proc;
        proc.onaudioprocess = (event) => {
          scriptHeard.current = true;
          storePcm(event.inputBuffer.getChannelData(0), rate);
        };
        src.connect(proc);
        proc.connect(gain);
      } catch {
        procRef.current = null;
      }
      try {
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        const buf = new Float32Array(analyser.fftSize);
        src.connect(analyser);
        analyser.connect(gain);
        pullRef.current = window.setInterval(() => {
          if (ctx.state === "suspended") void ctx.resume().catch(() => {});
          if (scriptHeard.current) return;
          analyser.getFloatTimeDomainData(buf);
          storePcm(buf, rate);
        }, 40);
      } catch {
        /* analyser only if ScriptProcessor stays silent */
      }
      gain.connect(ctx.destination);
    } catch {
      procRef.current = null;
    }
  };

  const flushPcm = useCallback(
    () =>
      new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          resolve();
        };
        const proc = procRef.current;
        if (!proc) {
          finish();
          return;
        }
        const prev = proc.onaudioprocess;
        proc.onaudioprocess = (event) => {
          if (prev) prev.call(proc, event);
          finish();
        };
        window.setTimeout(finish, 90);
      }),
    []
  );

  const stopClone = () => {
    const clone = cloneRef.current;
    cloneRef.current = null;
    if (!clone || clone === streamRef.current) return;
    clone.getTracks().forEach((t) => t.stop());
  };

  const unlockOrientation = () => {
    try {
      void screen.orientation?.unlock?.();
    } catch {
      /* ignore */
    }
  };

  const dropMic = () => {
    streamRef.current = null;
    releaseSharedMic();
    releaseTalkMic();
  };

  const fail = (reason: Exclude<TalkMicBlock, null>) => {
    startingRef.current = false;
    pendingStopRef.current = false;
    setCapturing(false);
    setBlock(reason);
    dropMic();
    setPhaseNow("denied");
  };

  const openMic = useCallback(async (): Promise<MediaStream | null> => {
    const stream = await armTalkMic();
    if (isLive(stream)) {
      streamRef.current = stream;
      grantedRef.current = true;
      setBlock(null);
    }
    return stream;
  }, []);

  const acquireStream = useCallback(async (): Promise<MediaStream | null> => {
    const avail = micAvailability();
    if (avail !== "ok") {
      fail(avail);
      return null;
    }
    const stream = await openMic();
    if (deadRef.current || pendingStopRef.current) {
      dropMic();
      return null;
    }
    if (!stream) {
      fail("permission");
      return null;
    }
    return stream;
  }, [openMic]);

  const stopRecorder = useCallback((keep: boolean): Promise<Blob | null> => {
    const deliver = (blob: Blob | null) => blob;
    startingRef.current = false;
    pendingStopRef.current = false;
    setCapturing(false);
    const rec = recRef.current;
    recRef.current = null;
    window.clearInterval(tick.current);
    unlockOrientation();
    const job = new Promise<Blob | null>((resolve) => {
      const publish = () => {
        const parts = pcmParts.current;
        pcmParts.current = [];
        const wav = wavFromPcm(parts);
        dropMic();
        stopPcm();
        pcmQueue.current = [];
        releaseWake();
        const duration = Date.now() - fingerDown.current;
        const mime = rec?.mimeType || chunks.current[0]?.type || "audio/mp4";
        const mr = chunks.current.length
          ? new Blob(chunks.current, { type: mime })
          : null;
        chunks.current = [];
        const blob =
          wav && wav.size >= 800
            ? wav
            : mr && mr.size >= 800
              ? mr
              : duration >= MIN_MS
                ? mr
                : null;
        talkProbe(`blob-${blob?.size || 0}`);
        if (!keep || !blob || (duration < MIN_MS && blob.size < 800)) {
          setBlob(null);
          setPhaseNow("idle");
          setElapsed(0);
          resolve(null);
          return;
        }
        setBlob(blob);
        setPhaseNow("review");
        resolve(blob);
        try {
          shipRef.current?.(blob);
        } catch {
          /* upload must not break the recorder */
        }
      };
      if (rec && rec.state !== "inactive") {
        let sealed = false;
        const seal = () => {
          if (sealed) return;
          sealed = true;
          waitChunk.current = null;
          window.clearTimeout(forced);
          publish();
        };
        rec.onstop = () => {
          if (chunks.current.length || pcmParts.current.length) seal();
          else {
            waitChunk.current = seal;
            window.setTimeout(seal, 1400);
          }
        };
        const forced = window.setTimeout(seal, 1500);
        try {
          rec.stop();
        } catch {
          seal();
        }
        return;
      }
      publish();
    });
    takenRef.current = job.then(deliver);
    return takenRef.current;
  }, []);

  const beginRecorder = (stream: MediaStream) => {
    const stillDown =
      pressedRef.current || touchesRef.current > 0 || touchHoldRef.current;
    if ((deadRef.current || pendingStopRef.current) && !stillDown) {
      if (deadRef.current) talkProbe("rec-dead");
      startingRef.current = false;
      pendingStopRef.current = false;
      pressedRef.current = false;
      setPressed(false);
      dropMic();
      stopPcm();
      pcmQueue.current = [];
      pcmParts.current = [];
      setCapturing(false);
      setPhaseNow("idle");
      return;
    }
    pendingStopRef.current = false;
    chunks.current = [];
    pcmQueue.current = [];
    pcmParts.current = [];
    cloneRef.current = null;
    const now = Date.now();
    started.current = now;
    if (!fingerDown.current) fingerDown.current = now;
    setBlob(null);
    setElapsed(0);
    talkProbe(iosCapture() ? "rec-ios" : "rec-file");
    if (iosCapture()) startPcm(stream);
    let fileRec = false;
    if (typeof MediaRecorder !== "undefined") {
      try {
        const mime = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"].find(
          (t) =>
            typeof MediaRecorder.isTypeSupported === "function" &&
            MediaRecorder.isTypeSupported(t)
        );
        const rec = mime
          ? new MediaRecorder(stream, { mimeType: mime })
          : new MediaRecorder(stream);
        rec.ondataavailable = (e) => {
          if (!e.data.size) return;
          chunks.current.push(e.data);
          chunkRef.current?.(e.data);
          waitChunk.current?.();
        };
        recRef.current = rec;
        try {
          rec.start(1000);
        } catch {
          rec.start();
        }
        fileRec = true;
        if (iosCapture()) talkProbe("rec-file");
      } catch {
        const broken = recRef.current;
        recRef.current = null;
        try {
          broken?.stop();
        } catch {
          /* ignore */
        }
        if (!iosCapture()) {
          stopClone();
          fail("unsupported");
          return;
        }
      }
    } else if (!iosCapture()) {
      fail("unsupported");
      return;
    }
    if (!fileRec && !procRef.current && !pullRef.current) {
      talkProbe("rec-fail");
      fail("unsupported");
      return;
    }
    const tracks = stream.getAudioTracks?.() ?? stream.getTracks();
    for (const t of tracks) {
      t.addEventListener?.("ended", () => {
        if (recRef.current && recRef.current.state !== "inactive")
          stopRecorder(true);
        else if (procRef.current || pullRef.current) stopRecorder(true);
      });
    }
    startingRef.current = false;
    setCapturing(true);
    setPhaseNow("holding");
    try {
      navigator.vibrate?.(12);
    } catch {
      /* ignore */
    }
    try {
      const nav = navigator as Navigator & {
        wakeLock?: {
          request: (t: string) => Promise<{ release: () => Promise<void> }>;
        };
      };
      void nav.wakeLock
        ?.request("screen")
        .then((lock) => {
          wakeRef.current = lock;
        })
        .catch(() => {
          wakeRef.current = null;
        });
    } catch {
      wakeRef.current = null;
    }
    try {
      const orient = screen.orientation as ScreenOrientation & {
        lock?: (mode: string) => Promise<void>;
      };
      void orient.lock?.("portrait");
    } catch {
      /* ignore */
    }
    if (!pressedRef.current) {
      stopRecorder(true);
      return;
    }
    tick.current = window.setInterval(() => {
      const ms = Date.now() - started.current;
      setElapsed(ms);
      if (ms >= MAX_MS) {
        window.clearInterval(tick.current);
        void flushPcm().then(() => stopRecorder(true));
      }
    }, 1000);
  };

  const start = useCallback(
    async (gum?: Promise<MediaStream>) => {
      if (
        phaseRef.current === "holding" &&
        (recRef.current || procRef.current || pullRef.current)
      )
        return;
      const avail = micAvailability();
      if (avail !== "ok") {
        fail(avail);
        return;
      }
      if (startingRef.current) {
        pendingStopRef.current = false;
        setPhaseNow("holding");
        return;
      }
      fingerDown.current = Date.now();
      if (isLive(sharedStream)) streamRef.current = sharedStream;
      if (isLive(streamRef.current) && streamRef.current) {
        beginRecorder(streamRef.current);
        return;
      }
      startingRef.current = true;
      pendingStopRef.current = false;
      let stream: MediaStream | null = null;
      if (gum) {
        try {
          stream = await gum;
        } catch (err) {
          const name = err instanceof Error && err.name ? err.name : "throw";
          talkProbe(`gum-${name}`);
        }
      } else {
        stream = await acquireStream();
      }
      if (stream) {
        watchEnded(stream);
        sharedStream = stream;
        streamRef.current = stream;
      }
      talkProbe(
        stream
          ? pressedRef.current || touchHoldRef.current
            ? "mic-held"
            : "mic-up"
          : "mic-none"
      );
      const stillDown =
        pressedRef.current || touchesRef.current > 0 || touchHoldRef.current;
      if (!stream || !stillDown) {
        if (!stream) fail("permission");
        pressedRef.current = false;
        setPressed(false);
        pendingStopRef.current = false;
        startingRef.current = false;
        setCapturing(false);
        if (stream) dropMic();
        return;
      }
      if (pendingStopRef.current) talkProbe("hide-ignored");
      pendingStopRef.current = false;
      beginRecorder(stream);
    },
    [acquireStream, stopRecorder]
  );

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    const abandon = () => {
      if (pressedRef.current || startingRef.current || touchesRef.current > 0) {
        talkProbe("hide-held");
        return;
      }
      pendingStopRef.current = true;
      if (recRef.current && recRef.current.state !== "inactive") {
        stopRecorder(true);
        return;
      }
      startingRef.current = false;
      setCapturing(false);
      dropMic();
      stopPcm();
      if (phaseRef.current === "holding") setPhaseNow("idle");
    };
    window.addEventListener("pagehide", abandon);
    return () => {
      window.removeEventListener("pagehide", abandon);
    };
  }, [stopRecorder]);

  const touchesRef = useRef(0);
  const touchHoldRef = useRef(false);

  const noteTouches = useCallback((count: number) => {
    touchesRef.current = count;
  }, []);

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      talkProbe(e.pointerType === "touch" ? "down-touch" : "down");
      touchHoldRef.current = e.pointerType === "touch";
      if (e.pointerType === "touch") {
        touchesRef.current = Math.max(touchesRef.current, 1);
        e.preventDefault();
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      }
      pressedRef.current = true;
      setPressed(true);
      if (isLive(sharedStream)) streamRef.current = sharedStream;
      unlockTalkPlayback();
      routeTalkMic();
      if (isLive(streamRef.current)) {
        void start();
        return;
      }
      if (iosCapture()) primeCaptureContext();
      const opened = micAvailability() === "ok" ? armTalkMic() : null;
      void start(
        opened
          ? opened.then((stream) => {
              if (!stream) throw new Error("no-mic");
              return stream;
            })
          : undefined
      );
    },
    [start]
  );

  const onPointerUp = useCallback(() => {
    if (touchesRef.current > 0) return false;
    touchHoldRef.current = false;
    pressedRef.current = false;
    setPressed(false);
    if (!recRef.current && !procRef.current && !pullRef.current) {
      pendingStopRef.current = true;
      startingRef.current = false;
      setCapturing(false);
      if (phaseRef.current === "holding") setPhaseNow("idle");
      return true;
    }
    if (phaseRef.current !== "holding") return true;
    try {
      navigator.vibrate?.(8);
    } catch {
      /* ignore */
    }
    stopRecorder(true);
    return true;
  }, [stopRecorder]);

  const setChunkHandler = useCallback((fn: ((blob: Blob) => void) | null) => {
    chunkRef.current = fn;
  }, []);

  const setPcmHandler = useCallback(
    (fn: ((pcm: ArrayBuffer) => void) | null) => {
      pcmRef.current = fn;
      if (!fn) return;
      const queued = pcmQueue.current.splice(0);
      for (const buf of queued) fn(buf);
    },
    []
  );

  const onClick = useCallback(() => {
    if (startingRef.current) return;
    if (phaseRef.current === "holding" || phaseRef.current === "review") return;
    if (
      grantedRef.current &&
      isLive(streamRef.current) &&
      phaseRef.current !== "denied"
    )
      return;
    void acquireStream().then((stream) => {
      if (!stream || phaseRef.current !== "denied") return;
      dropMic();
      setBlock(null);
      setPhaseNow("idle");
    });
  }, [acquireStream]);

  const discard = useCallback(() => {
    setBlob(null);
    setElapsed(0);
    setCapturing(false);
    setPhaseNow(phaseRef.current === "denied" ? "denied" : "idle");
  }, []);

  useEffect(() => {
    deadRef.current = false;
    micOwners += 1;
    return () => {
      deadRef.current = true;
      window.clearInterval(tick.current);
      try {
        recRef.current?.stop();
      } catch {
        /* ignore */
      }
      stopPcm();
      stopClone();
      releaseWake();
      unlockOrientation();
      micOwners -= 1;
      if (micOwners <= 0) {
        micOwners = 0;
        window.setTimeout(() => {
          if (micOwners <= 0) releaseSharedMic();
        }, 0);
      }
    };
  }, []);

  return {
    phase,
    pressed,
    capturing,
    block,
    elapsed,
    blob,
    showTimer: elapsed >= TIMER_MS,
    onPointerDown,
    onPointerUp,
    noteTouches,
    onPointerCancel: onPointerUp,
    onClick,
    pullRecording: () => takenRef.current,
    discard,
    arm: openMic,
    setChunkHandler,
    setPcmHandler,
    setShip: (fn: ((blob: Blob) => void) | null) => {
      shipRef.current = fn;
    },
    flushPcm,
  };
}
